/**
 * The rundown tree's keyboard model, as a pure reducer: a key press and the
 * visible rows in, the next focus/selection/editing state and the command it
 * asks for out. The tree component runs it; what a command MEANS (a patch, a
 * confirmation dialog) is the view's.
 *
 *     ↑ / ↓            move focus (Shift: extend the selection)
 *     PgUp / PgDn      ten rows up / down (Shift: extend)
 *     Home / End       first / last row
 *     ← / →            collapse, or go to the parent / expand, or go to the first child
 *     Enter / F2       edit the focused row's name inline
 *     Esc              cancel the inline edit, else clear the selection to the focus
 *     Del / Backspace  delete the selection (the view confirms)
 *     Ctrl/⌘+Z         undo;  Ctrl/⌘+Shift+Z or Ctrl+Y  redo
 *     Ctrl/⌘+Enter     new row of the focused kind after it (a segment when the tree is empty)
 *     Ctrl/⌘+Shift+Enter  new child of the focused row (a part in a segment, a piece in a part)
 *     Space            toggle the focused row in the selection
 *     Alt+↑ / Alt+↓    move the selection one slot up / down (into the
 *                      neighbouring parent at the edge — `move.ts`)
 *     Alt+Shift+↑ / ↓  move the selection into the previous / next parent
 *
 * While a row is being edited inline, the input owns the keys; the reducer
 * only sees Esc (cancel).
 */
import type { MoveDirection } from './move.js';
import type { TreeKind } from './viewModel.js';

export type NavRow = {
  readonly id: string;
  readonly kind: TreeKind;
  readonly parentId: string | null;
  readonly hasChildren: boolean;
  readonly collapsed: boolean;
};

export type NavState = {
  readonly focusedId: string | null;
  /** Selected row ids; the focused row is always selected unless nothing is focused. */
  readonly selected: readonly string[];
  /** Where a Shift range starts. */
  readonly anchorId: string | null;
  /** The row whose name is being edited inline, or null. */
  readonly editingId: string | null;
};

export type KeyInput = { readonly key: string; readonly shift: boolean; readonly mod: boolean; readonly alt: boolean };

export type TreeCommand =
  | { readonly type: 'delete'; readonly ids: readonly string[] }
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  | { readonly type: 'create'; readonly kind: TreeKind; readonly parentId: string | null; readonly afterId: string | null }
  | { readonly type: 'toggle-collapse'; readonly id: string }
  | { readonly type: 'move'; readonly ids: readonly string[]; readonly direction: MoveDirection };

export type KeyResult = { readonly state: NavState; readonly command: TreeCommand | null; readonly handled: boolean };

export const EMPTY_NAV: NavState = { focusedId: null, selected: [], anchorId: null, editingId: null };

const CHILD_KIND: Readonly<Record<TreeKind, TreeKind | null>> = { segment: 'part', part: 'piece', piece: null, adlib: null };

/** How far PgUp / PgDn move the focus. */
export const PAGE_ROWS = 10;

function focus(state: NavState, id: string): NavState {
  return { ...state, focusedId: id, selected: [id], anchorId: id };
}

function range(rows: readonly NavRow[], fromId: string, toId: string): string[] {
  const a = rows.findIndex((r) => r.id === fromId);
  const b = rows.findIndex((r) => r.id === toId);
  if (a < 0 || b < 0) return [toId];
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return rows.slice(lo, hi + 1).map((r) => r.id);
}

function moveTo(state: NavState, rows: readonly NavRow[], index: number, extend: boolean): NavState {
  const target = rows[Math.max(0, Math.min(rows.length - 1, index))];
  if (target === undefined) return state;
  if (!extend) return focus(state, target.id);
  const anchor = state.anchorId ?? state.focusedId ?? target.id;
  return { ...state, focusedId: target.id, selected: range(rows, anchor, target.id), anchorId: anchor };
}

function unhandled(state: NavState): KeyResult {
  return { state, command: null, handled: false };
}

function done(state: NavState, command: TreeCommand | null): KeyResult {
  return { state, command, handled: true };
}

function horizontal(state: NavState, rows: readonly NavRow[], current: NavRow, index: number, right: boolean): KeyResult {
  if (right) {
    if (current.hasChildren && current.collapsed) return done(state, { type: 'toggle-collapse', id: current.id });
    const next = rows[index + 1];
    return current.hasChildren && next !== undefined && next.parentId === current.id ? done(focus(state, next.id), null) : done(state, null);
  }
  if (current.hasChildren && !current.collapsed) return done(state, { type: 'toggle-collapse', id: current.id });
  const parent = rows.find((r) => r.id === current.parentId);
  return parent === undefined ? done(state, null) : done(focus(state, parent.id), null);
}

function createCommand(current: NavRow | undefined, child: boolean): TreeCommand | null {
  if (current === undefined) return { type: 'create', kind: 'segment', parentId: null, afterId: null };
  if (!child) return { type: 'create', kind: current.kind, parentId: current.parentId, afterId: current.id };
  const kind = CHILD_KIND[current.kind];
  return kind === null ? null : { type: 'create', kind, parentId: current.id, afterId: null };
}

function modified(state: NavState, current: NavRow | undefined, key: KeyInput): KeyResult {
  const lower = key.key.toLowerCase();
  if (lower === 'z') return done(state, { type: key.shift ? 'redo' : 'undo' });
  if (lower === 'y') return done(state, { type: 'redo' });
  if (key.key === 'Enter') return done(state, createCommand(current, key.shift));
  return unhandled(state);
}

function selectionOrFocus(state: NavState): readonly string[] {
  if (state.selected.length > 0) return state.selected;
  return state.focusedId === null ? [] : [state.focusedId];
}

function navigation(state: NavState, rows: readonly NavRow[], key: KeyInput, index: number, current: NavRow | undefined): KeyResult | null {
  const from = current === undefined ? 0 : index;
  switch (key.key) {
    case 'ArrowDown':
      return done(moveTo(state, rows, current === undefined ? 0 : from + 1, key.shift), null);
    case 'ArrowUp':
      return done(moveTo(state, rows, current === undefined ? 0 : from - 1, key.shift), null);
    case 'PageDown':
      return done(moveTo(state, rows, current === undefined ? 0 : from + PAGE_ROWS, key.shift), null);
    case 'PageUp':
      return done(moveTo(state, rows, current === undefined ? 0 : from - PAGE_ROWS, key.shift), null);
    case 'Home':
      return done(moveTo(state, rows, 0, key.shift), null);
    case 'End':
      return done(moveTo(state, rows, rows.length - 1, key.shift), null);
    case 'ArrowRight':
    case 'ArrowLeft':
      return current === undefined ? unhandled(state) : horizontal(state, rows, current, index, key.key === 'ArrowRight');
    default:
      return null;
  }
}

function toggleSelected(state: NavState, current: NavRow): NavState {
  const has = state.selected.includes(current.id);
  return { ...state, selected: has ? state.selected.filter((id) => id !== current.id) : [...state.selected, current.id], anchorId: current.id };
}

function plain(state: NavState, rows: readonly NavRow[], key: KeyInput, index: number, current: NavRow | undefined): KeyResult {
  const moved = navigation(state, rows, key, index, current);
  if (moved !== null) return moved;
  switch (key.key) {
    case 'Enter':
    case 'F2':
      return current === undefined ? unhandled(state) : done({ ...state, editingId: current.id }, null);
    case 'Escape':
      return done(state.focusedId === null ? EMPTY_NAV : focus(state, state.focusedId), null);
    case 'Delete':
    case 'Backspace': {
      const ids = selectionOrFocus(state);
      return ids.length === 0 ? unhandled(state) : done(state, { type: 'delete', ids });
    }
    case ' ':
      return current === undefined ? unhandled(state) : done(toggleSelected(state, current), null);
    default:
      return unhandled(state);
  }
}

/** Alt+arrows: move the selection (in display order), never the focus. */
function moveKeys(state: NavState, rows: readonly NavRow[], key: KeyInput): KeyResult {
  if (key.key !== 'ArrowUp' && key.key !== 'ArrowDown') return unhandled(state);
  const chosen = new Set(selectionOrFocus(state));
  const ids = rows.filter((r) => chosen.has(r.id)).map((r) => r.id);
  if (ids.length === 0) return unhandled(state);
  const up = key.key === 'ArrowUp';
  const direction: MoveDirection = key.shift ? (up ? 'prev-parent' : 'next-parent') : up ? 'up' : 'down';
  return done(state, { type: 'move', ids, direction });
}

export function reduceKey(state: NavState, rows: readonly NavRow[], key: KeyInput): KeyResult {
  if (state.editingId !== null) {
    return key.key === 'Escape' ? done({ ...state, editingId: null }, null) : unhandled(state);
  }
  const index = state.focusedId === null ? -1 : rows.findIndex((r) => r.id === state.focusedId);
  const current = index < 0 ? undefined : rows[index];
  if (key.mod && !key.alt) return modified(state, current, key);
  if (key.alt && !key.mod) return moveKeys(state, rows, key);
  if (key.mod || key.alt) return unhandled(state);
  return plain(state, rows, key, index, current);
}

/** A click: plain focuses; Shift extends from the anchor; Ctrl/⌘ toggles. */
export function reduceClick(state: NavState, rows: readonly NavRow[], id: string, shift: boolean, mod: boolean): NavState {
  if (shift) {
    const anchor = state.anchorId ?? state.focusedId ?? id;
    return { ...state, focusedId: id, selected: range(rows, anchor, id), anchorId: anchor, editingId: null };
  }
  if (mod) {
    const has = state.selected.includes(id);
    return { ...state, focusedId: id, selected: has ? state.selected.filter((s) => s !== id) : [...state.selected, id], anchorId: id, editingId: null };
  }
  return { ...focus(state, id), editingId: null };
}

/** Drop ids that are no longer visible (deleted, collapsed away) from the state. */
export function pruneNav(state: NavState, rows: readonly NavRow[]): NavState {
  const visible = new Set(rows.map((r) => r.id));
  const selected = state.selected.filter((id) => visible.has(id));
  const focusedId = state.focusedId !== null && visible.has(state.focusedId) ? state.focusedId : null;
  const editingId = state.editingId !== null && visible.has(state.editingId) ? state.editingId : null;
  const anchorId = state.anchorId !== null && visible.has(state.anchorId) ? state.anchorId : null;
  if (selected.length === state.selected.length && focusedId === state.focusedId && editingId === state.editingId && anchorId === state.anchorId) return state;
  return { focusedId, selected, anchorId, editingId };
}

/**
 * Where the focus goes after `ids` are deleted: the first visible row after
 * them that survives (not deleted, not under a deleted row), else the last
 * one before them, else nothing — so the keyboard user stays in the tree
 * instead of losing the focus with the row.
 */
export function focusAfterDelete(rows: readonly NavRow[], ids: readonly string[]): NavState {
  const removed = new Set(ids);
  for (const row of rows) if (row.parentId !== null && removed.has(row.parentId)) removed.add(row.id);
  const indexes = rows.flatMap((r, i) => (ids.includes(r.id) ? [i] : []));
  if (indexes.length === 0) return EMPTY_NAV;
  const first = Math.min(...indexes);
  const last = Math.max(...indexes);
  const after = rows.slice(last + 1).find((r) => !removed.has(r.id));
  const before = rows
    .slice(0, first)
    .reverse()
    .find((r) => !removed.has(r.id));
  const target = after ?? before;
  return target === undefined ? EMPTY_NAV : focus(EMPTY_NAV, target.id);
}

function ancestorOfKind(rows: readonly NavRow[], from: NavRow, kind: TreeKind): NavRow | null {
  let current: NavRow | undefined = from;
  const seen = new Set<string>();
  while (current !== undefined && !seen.has(current.id)) {
    if (current.kind === kind) return current;
    seen.add(current.id);
    const parentId: string | null = current.parentId;
    current = rows.find((r) => r.id === parentId);
  }
  return null;
}

const PARENT_KIND: Readonly<Record<TreeKind, TreeKind | null>> = { segment: null, part: 'segment', piece: 'part', adlib: 'part' };

/**
 * "New <kind>" from a toolbar or context menu, relative to the focused row:
 * after the focused row's own `kind` ancestor (itself included), else as the
 * last child of its `kind`'s parent-kind ancestor, else — for a segment — at
 * the end of the rundown. An adlib with no part in focus is a global adlib
 * (under the rundown, `parentId` null like a segment). Null when there is
 * nowhere for it (a part with no segment in focus).
 */
export function createCommandFor(kind: TreeKind, focused: NavRow | null, rows: readonly NavRow[]): TreeCommand | null {
  const same = focused === null ? null : ancestorOfKind(rows, focused, kind);
  if (same !== null) return { type: 'create', kind, parentId: same.parentId, afterId: same.id };
  const parentKind = PARENT_KIND[kind];
  if (parentKind === null) return { type: 'create', kind, parentId: null, afterId: null };
  const parent = focused === null ? null : ancestorOfKind(rows, focused, parentKind);
  if (parent === null) return kind === 'adlib' ? { type: 'create', kind, parentId: null, afterId: null } : null;
  return { type: 'create', kind, parentId: parent.id, afterId: null };
}

/** The keyboard model's view of the flattened tree. */
export function navRows(rows: readonly { readonly id: string; readonly parentId: string | null; readonly view: { readonly kind: TreeKind }; readonly hasChildren: boolean; readonly collapsed: boolean }[]): NavRow[] {
  return rows.map((r) => ({ id: r.id, kind: r.view.kind, parentId: r.parentId, hasChildren: r.hasChildren, collapsed: r.collapsed }));
}
